import { useState, useEffect, useCallback } from "react";
import { withAppHOC } from "../../hoc";
import PageHeading from "../../components/PageHeader/PageHeader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import {
  MessageBox,
  Label,
  Fieldset,
  TextInput,
  Button,
  Form,
  ResetButton,
  CustomButton,
  Dropdown,
} from "../../components/FormElements";
import { TableBuilder } from "../../components/TableElements";
import {
  StyledFiltersContainer,
  StyledApiCallLogsContainer,
  StyledBodyContainer,
  StyledHeading,
  StyledContent,
} from "../../components/Styled";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { API_RESPONSE_TYPES } from "../../constants";
import { getSearchParams, shouldRedirect, redirectToPageOne } from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";
import Modal from "../../components/Modal";
import Accordination from "../../components/Accordination/Accordination";
import JSONPretty from "react-json-pretty";
import { faEye } from "@fortawesome/free-solid-svg-icons";

const AlertHistory = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 200,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [medium, setMedium] = useState("");
  const [notificationType, setNotificationType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalType, setModalType] = useState("");
  const [modalId, setModalId] = useState("");

  const resetForm = () => {
    setMedium("");
    setNotificationType("");
    setStartDate("");
    setEndDate("");
    readData();
  };

  const readData = useCallback(
    (medium = "", notificationType = "", startDate = "", endDate = "") => {
      setIsLoading(true);
      setResponseStatus("");
      setResponseMessage("");
      API.get(
        `/reports/alert-history/?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&medium=${medium}&notificationType=${notificationType}&startDate=${startDate}&endDate=${endDate}`
      )
        .then((response) => {
          const { status, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.SUCCESS) {
              setTotalRecords(pageInfo.totalRecords);
            }
            setAlerts(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILURE);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  // Show modal only if request exists
  const toggleModal = (id) => {
    const selectedAlert = alerts.find((alert) => alert.id === id && alert.request);
    if (!selectedAlert) return; // Do nothing if no request

    setModalData([selectedAlert]);
    setModalType("LOGS");
    setModalId(id);
    setShowModal(true);
  };

  const reloadData = () => {
    readData(medium, notificationType, startDate, endDate);
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    if (!isFiltering) {
      readData(medium, notificationType, startDate, endDate);
    }
  }, [paginationData, readData]);

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => setShowFilters((prev) => !prev);

  return (
    <>
      <PageHeading {...props} reloadData={reloadData} />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? "show" : ""}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>Medium</Label>
            <Dropdown
              style={{ border: "1px solid #cacaca" }}
              placeholder="Medium"
              value={medium}
              onChange={setMedium}
              options={[
                { title: "-- SELECT MEDIUM --", value: "" },
                { title: "Whatsapp", value: "Whatsapp" },
                { title: "SMS", value: "SMS" },
                { title: "Email", value: "Email" },
              ]}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>Notification Type</Label>
            <Dropdown
              style={{ border: "1px solid #cacaca" }}
              placeholder="Notification Type"
              value={notificationType}
              onChange={setNotificationType}
              options={[
                { title: "-- SELECT NOTIFICATION TYPE --", value: "" },
                { title: "Complaint", value: "Complaint" },
                { title: "Pending", value: "Pending" },
                { title: "Seller", value: "Seller" },
                { title: "Others", value: "Others" },
              ]}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>Start Date</Label>
            <TextInput
              type="date"
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>End Date</Label>
            <TextInput
              type="date"
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">
              Filter
            </Button>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>

      <LoadingSpinner
        isLoading={responseStatus === "" && alerts?.length === 0 && isLoading}
      />
      <NoData
        status={responseStatus !== API_RESPONSE_TYPES.FAILURE && !isLoading && alerts?.length === 0}
        message={`No alerts found`}
      />
      <MessageBox status={responseStatus} message={responseMessage} />

      {alerts?.length > 0 && (
        <>
          <TableBuilder
            isLoading={alerts?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: "Logs",
                dataSelector: "id",
                dataType: "string",
                CellRenderer: (value, row) =>
                  row.request ? (
                    <CustomButton
                      icon={faEye}
                      onClick={() => toggleModal(value)}
                      style={{ color: "orange" }}
                      disabled={isLoading}
                    />
                  ) : null,
              },
              { title: "Date/Time", dataSelector: "createdOn", dataType: "string" },
              { title: "Receiver", dataSelector: "receiver", dataType: "string" },
              { title: "Medium", dataSelector: "medium", dataType: "string" },
              { title: "Notification Type", dataSelector: "notificationType", dataType: "string" },
              {
                title: "Message",
                dataSelector: "notificationMessage",
                dataType: "string",
                CellRenderer: (value) => value || "N/A",
              },
            ]}
            tableData={alerts}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}

      {/* LOGS Modal */}
      <Modal
        modalStatus={showModal}
        setModalStatus={() => {
          setShowModal(false);
          setModalData([]);
          setModalType("");
          setModalId("");
          setResponseStatus("");
          setResponseMessage("");
        }}
        hideCloseButton
      >
        <StyledApiCallLogsContainer>
          <h3>API Call Logs</h3>
          <LoadingSpinner isLoading={responseStatus === "" && modalData?.length === 0 && isLoading} />
          <NoData
            status={responseStatus !== API_RESPONSE_TYPES.FAILURE && !isLoading && modalData?.length === 0}
            message="No API logs found"
          />
          <MessageBox status={responseStatus} message={responseMessage} />
          {modalData?.length > 0 &&
            modalData.map(({ requestType, request, requestOn, requestBody, response, responseOn }, index) => (
              <Accordination key={index} header={`${requestType}`} autoExpanded={true}>
                <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>
                  <StyledBodyContainer>
                    <StyledHeading>API Request {requestOn && `( ${requestOn} )`}</StyledHeading>
                    <StyledContent style={{ background: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                      <JSONPretty data={request}></JSONPretty>
                    </StyledContent>
                    {requestBody && (
                      <>
                        <StyledHeading>API Request Body</StyledHeading>
                        <StyledContent style={{ background: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                          <JSONPretty data={requestBody}></JSONPretty>
                        </StyledContent>
                      </>
                    )}
                  </StyledBodyContainer>
                  <StyledBodyContainer>
                    <StyledHeading>API Response {responseOn && `( ${responseOn} )`}</StyledHeading>
                    <StyledContent style={{ background: "#f4f4f4", padding: "10px", borderRadius: "4px" }}>
                      <JSONPretty data={response}></JSONPretty>
                    </StyledContent>
                  </StyledBodyContainer>
                </div>
              </Accordination>
            ))}
        </StyledApiCallLogsContainer>
      </Modal>
    </>
  );
};

export default withAppHOC(AlertHistory);
